/**
 * Centralized API endpoint constants.
 * Single source of truth for all API paths used across the frontend.
 */

const V1 = '/api/v1';

const API_ENDPOINTS = Object.freeze({
  // Auth
  AUTH: Object.freeze({
    TOKEN: `${V1}/auth/token`,
    REGISTER: `${V1}/auth/register`,
    LOGOUT: `${V1}/auth/logout`,
    REFRESH_TOKEN: `${V1}/auth/refresh-token`,
    CHANGE_PASSWORD: `${V1}/auth/change-password`,
    USERS_ME: `${V1}/users/me`,
    UPDATE_PROFILE: `${V1}/users/me/profile`,
    UPDATE_TIMEZONE: `${V1}/users/me/timezone`,
    ORGANIZATIONS: `${V1}/organizations`,
    ORGANIZATION: id => `${V1}/organizations/${id}`,
    VERIFY_EMAIL: `${V1}/auth/verify-email`,
    RESEND_VERIFICATION: `${V1}/auth/resend-verification`,
  }),

  // Third-party (Etsy OAuth)
  THIRD_PARTY: Object.freeze({
    VERIFY_CONNECTION: `${V1}/third-party/verify-connection`,
    // DB-only tri-state. Unlike VERIFY_CONNECTION it costs no Etsy quota and
    // can tell "reconnect required" apart from "never connected".
    CONNECTION_STATUS: `${V1}/third-party/connection-status`,
    OAUTH_REDIRECT: `${V1}/third-party/oauth-redirect`,
    OAUTH_DATA: `${V1}/third-party/oauth-data`,
    REFRESH_TOKEN: `${V1}/third-party/refresh-token`,
    REVOKE_TOKEN: `${V1}/third-party/revoke-token`,
    SHIPPING_PROFILE: `${V1}/third-party/shipping-profile`,
  }),

  // Platform Connections (multi-shop management)
  PLATFORM_CONNECTIONS: Object.freeze({
    LIST: `${V1}/platform-connections`,
    ETSY_STORES: `${V1}/platform-connections/etsy/stores`,
    ETSY_STORE: id => `${V1}/platform-connections/etsy/stores/${id}`,
    SHOPIFY_STORES: `${V1}/platform-connections/shopify/stores`,
    SHOPIFY_STORE: id => `${V1}/platform-connections/shopify/stores/${id}`,
    VERIFY_ALL: `${V1}/platform-connections/verify-all`,
  }),

  // Third-party listings
  THIRD_PARTY_LISTINGS: Object.freeze({
    BASE: `${V1}/third-party-listings`,
    ALL: `${V1}/third-party-listings/all`,
    OPTIONS_TAXONOMIES: `${V1}/third-party-listings/options/taxonomies`,
    OPTIONS_SHIPPING_PROFILES: `${V1}/third-party-listings/options/shipping-profiles`,
    OPTIONS_SHOP_SECTIONS: `${V1}/third-party-listings/options/shop-sections`,
    OPTIONS_READINESS_STATES: `${V1}/third-party-listings/options/readiness-states`,
    BULK_UPDATE: `${V1}/third-party-listings/bulk-update`,
    BULK_DELETE: `${V1}/third-party-listings/bulk-delete`,
    // "Full resync" — the uncapped sweep that ALSO tombstones listings deleted
    // on Etsy. Self-scoped: the shop comes from the JWT, so it takes no user id
    // and every seller may call it. Deliberately NOT the design-remap admin
    // route it used to post to — that one is super_admin-only, and since every
    // seller holds `owner` the button 403'd for all of them.
    REFRESH_FULL: `${V1}/third-party-listings/refresh-full`,
  }),

  // Dashboard
  DASHBOARD: Object.freeze({
    TOP_SELLERS: `${V1}/dashboard/top-sellers`,
    ANALYTICS: `${V1}/dashboard/analytics`,
    // Profit/revenue per day-week-month-year over a date range + best-selling
    // products and categories (shared cross-platform analytics contract).
    SALES: `${V1}/dashboard/sales`,
    // Per-item cost breakdown over a date range, grouped by section and template.
    COST_BREAKDOWN: `${V1}/dashboard/cost-breakdown`,
    PAYMENT_SUMMARY: `${V1}/dashboard/payment-summary`,
    // On-demand sync for the current shop (POST, 202) and the stored sync state it
    // reports (GET, pure DB read). Contract pinned in #1026.
    ETSY_ANALYTICS_SYNC: `${V1}/dashboard/etsy-analytics/sync`,
    ETSY_ANALYTICS_STATUS: `${V1}/dashboard/etsy-analytics/status`,
    AD_SPEND: `${V1}/dashboard/ad-spend`,
    AD_SPEND_IMPORT: `${V1}/dashboard/ad-spend/import`,
    LISTING_DETAIL: `${V1}/dashboard/listing`,
    SECTION_COSTS: `${V1}/dashboard/section-costs`,
    SECTION_COSTS_SECTIONS: `${V1}/dashboard/section-costs/sections`,
  }),

  // Settings (product templates, canvas, sizes)
  SETTINGS: Object.freeze({
    PRODUCT_TEMPLATE: `${V1}/settings/product-template`,
    PRODUCT_TEMPLATE_DEFAULT: `${V1}/settings/product-template/default`,
    SHOPIFY_PRODUCT_TEMPLATE: `${V1}/settings/shopify-product-template`,
    CRAFTFLOW_COMMERCE_TEMPLATE: `${V1}/settings/craftflow-commerce-template`,
    ALL_TEMPLATES: `${V1}/settings/all-templates`,
    CANVAS_CONFIG: `${V1}/settings/canvas-config`,
    SIZE_CONFIG: `${V1}/settings/size-config`,
    TAXONOMIES: `${V1}/settings/product-template/taxonomies`,
    SHOP_SECTIONS: `${V1}/settings/product-template/shop-sections`,
    RETURN_POLICIES: `${V1}/settings/product-template/return-policies`,
  }),

  // Designs
  DESIGNS: Object.freeze({
    LIST: `${V1}/designs/list`,
    STATS: `${V1}/designs/stats`,
    START_UPLOAD: `${V1}/designs/start-upload`,
    // Chunked upload protocol (init → chunk* → finalize)
    UPLOAD_BATCH_INIT: `${V1}/designs/upload-batches/init`,
    UPLOAD_BATCH_CHUNK: jobId => `${V1}/designs/upload-batches/${jobId}/chunk`,
    UPLOAD_BATCH_FINALIZE: jobId => `${V1}/designs/upload-batches/${jobId}/finalize`,
  }),

  // Orders (Etsy)
  ORDERS: Object.freeze({
    LIST: `${V1}/orders/`,
    ALL: `${V1}/orders/all-orders`,
    IMPORT_CSV: `${V1}/orders/import-csv`,
    PRINT_FILES: `${V1}/orders/create-print-files`,
    DOWNLOAD_PRINT_FILES: `${V1}/orders/download-print-files`,
    PRINT_FROM_SELECTION: `${V1}/orders/print-files-from-selection`,
    MANUAL_GANG_SHEET: `${V1}/orders/manual-gang-sheet`,
    PRINT_JOBS: `${V1}/orders/print-jobs`,
    PRINT_JOBS_PRINTER_METHOD: `${V1}/orders/print-jobs/printer-method`,
    // POST — send an already-rendered printfile to PrintMade (the seller's
    // post-render choice). Same id space and ownership check as the download.
    PRINTMADE_SEND: downloadId => `${V1}/orders/download-print-files/${downloadId}/printmade`,
    PRINT_BY_PRINTER_METHOD: `${V1}/orders/print-files-by-printer-method`,
    PRINT_STATUS: `${V1}/orders/status/print-tracking`,
    MARK_PRINTED: `${V1}/orders/status/mark-printed`,
    MARK_PACKED: `${V1}/orders/status/mark-packed`,
    // ODP (OnDemandPrintfiles) — a separate, seller-opt-in print-fulfillment
    // vendor from Send To PrintMade: ODP does its own layout/rendering from
    // pushed order-line data instead of CraftFlow rendering a zip. STATUS is
    // `{enabled, configured}` for the calling seller — both must be true
    // before the push action is shown. PUSH sends the selected orders' line
    // items for one template to ODP's push-intake API.
    ODP_STATUS: `${V1}/orders/odp/status`,
    ODP_PUSH: `${V1}/orders/odp/push-selected-orders`,
  }),

  // Etsy Customers
  ETSY_CUSTOMERS: Object.freeze({
    LIST: `${V1}/etsy/customers`,
    DETAIL: buyerUserId => `${V1}/etsy/customers/${buyerUserId}/detail`,
    ORDERS: buyerUserId => `${V1}/etsy/customers/${buyerUserId}/orders`,
    REVIEWS: buyerUserId => `${V1}/etsy/customers/${buyerUserId}/reviews`,
    PROFILE: buyerUserId => `${V1}/etsy/customers/${buyerUserId}/profile`,
    CATEGORIZE: `${V1}/etsy/customers/categorize-listings`,
  }),

  // Packing Slips
  PACKING_SLIP: Object.freeze({
    GENERATE: `${V1}/packing-slip/generate`,
    BULK_ETSY: `${V1}/packing-slip/bulk/etsy-orders`,
    BULK_SELECTED: `${V1}/packing-slip/bulk/selected-orders`,
    SHOPIFY_BULK_SELECTED: `${V1}/packing-slip/shopify/bulk/selected-orders`,
  }),

  // Packing Slip Templates
  PACKING_SLIP_TEMPLATES: Object.freeze({
    LIST: `${V1}/ecommerce/admin/packing-slip-templates/`,
    DEFAULT: `${V1}/ecommerce/admin/packing-slip-templates/default`,
    DETAIL: id => `${V1}/ecommerce/admin/packing-slip-templates/${id}`,
    PREVIEW: id => `${V1}/ecommerce/admin/packing-slip-templates/${id}/preview`,
    PREVIEW_HTML: `${V1}/ecommerce/admin/packing-slip-templates/preview-html`,
    VARIANTS: `${V1}/ecommerce/admin/packing-slip-templates/variants`,
  }),

  // Per-shop payments (Stripe Connect / bring-your-own keys)
  PAYMENTS: Object.freeze({
    SETTINGS: `${V1}/ecommerce/admin/payments/settings`,
    MODE: `${V1}/ecommerce/admin/payments/mode`,
    CONNECT_ONBOARD: `${V1}/ecommerce/admin/payments/connect/onboard-link`,
    CONNECT_STATUS: `${V1}/ecommerce/admin/payments/connect/status`,
    BYO_KEYS: `${V1}/ecommerce/admin/payments/byo-keys`,
    FEE: `${V1}/ecommerce/admin/payments/fee`,
  }),

  // Mockups
  MOCKUPS: Object.freeze({
    LIST: `${V1}/mockups/`,
    UPLOAD: `${V1}/mockups/upload`,
    // Bulk Etsy listing upload — async job pattern. POST returns
    // 202 + { job_id, status: "queued" } and the actual work runs
    // in a backend thread; clients poll UPLOAD_JOB until terminal.
    UPLOAD_MOCKUP: `${V1}/mockups/upload-mockup`,
    UPLOAD_JOB: jobId => `${V1}/mockups/upload-jobs/${jobId}`,
    // List user's in-flight + recently-terminal jobs. Drives the
    // app-level notification watcher (useUploadJobsWatcher).
    UPLOAD_JOBS_LIST: `${V1}/mockups/upload-jobs`,
    // Async design upload: returns 202 + job_id. Worker processes
    // the files (and optionally chains an Etsy listing job). Frontend
    // closes the modal on 202 and watches via the polling hook.
    DESIGNS_UPLOAD_ASYNC: `${V1}/designs/upload-async`,
    DESIGN_UPLOAD_JOB: jobId => `${V1}/designs/upload-jobs/${jobId}`,
    DESIGN_UPLOAD_JOBS_LIST: `${V1}/designs/upload-jobs`,
    IMAGES: `${V1}/mockups/images`, // append /{id}/mask-data
    MASK_DATA: imageId => `${V1}/mockups/images/${imageId}/mask-data`,
    GROUP: `${V1}/mockups/group`,
    TEMPLATE_TYPES: `${V1}/mockups/template-types`,
    // Per-user watermark library for the redesigned editor.
    WATERMARKS: `${V1}/mockups/watermarks`,
    WATERMARK: id => `${V1}/mockups/watermarks/${id}`,
    WATERMARK_UPLOAD: `${V1}/mockups/watermarks/upload`,
    WATERMARK_FILE: id => `${V1}/mockups/watermarks/${id}/file`,
    USER_TEMPLATES: `${V1}/user-templates`,
    USER_MASK_DATA: `${V1}/user-mask-data`, // append /{templateName}
    BASE_MOCKUPS: `${V1}/base-mockups`, // append /{template}
    MASKS: `${V1}/masks`,
    PREVIEW_GENERATE: `${V1}/mockups/preview-generate`,
    // Edit mode (#1791): read the saved mockup, stage a base photo, save everything in one PUT.
    BY_ID: id => `${V1}/mockups/by-id/${id}`,
    EDIT: id => `${V1}/mockups/${id}/edit`,
    STAGE_BASE: id => `${V1}/mockups/${id}/base-images/stage`,
    // Linked products per platform (#1791 S3). The stale-edit token (spec_version) now comes from BY_ID.
    PRODUCTS: id => `${V1}/mockups/${id}/products`,
    PRODUCT_LINK: id => `${V1}/mockups/${id}/products/link`,
    // Account-wide, insert-only scan of local data that links existing products to their mockups.
    GENERATION_BACKFILL: `${V1}/mockups/generation-records/backfill`,
    // Which mockup made each of these products (batch, read-only, <=500 refs per call).
    GENERATION_LOOKUP: `${V1}/mockups/generation-records/lookup`,
    // Replace one product's images with the seller's own files (multipart).
    RECREATE_UPLOAD: `${V1}/recreate-jobs/upload`,
    // Regenerate the images of listings that use a mockup (#1792): estimate, then start.
    RECREATE_ESTIMATE: id => `${V1}/mockups/${id}/recreate/estimate`,
    RECREATE: id => `${V1}/mockups/${id}/recreate`,
  }),

  // Regenerate-mockups jobs (#1792). GET takes ?status=&limit=&offset=.
  RECREATE_JOBS: Object.freeze({
    LIST: `${V1}/recreate-jobs`,
    GET: id => `${V1}/recreate-jobs/${id}`,
    PUBLISH: id => `${V1}/recreate-jobs/${id}/publish`,
    DISCARD: id => `${V1}/recreate-jobs/${id}/discard`,
    CANCEL: id => `${V1}/recreate-jobs/${id}/cancel`,
    UNDO: id => `${V1}/recreate-jobs/${id}/undo`,
    REPORT: id => `${V1}/recreate-jobs/${id}/report.csv`,
    EVENTS: id => `${V1}/recreate-jobs/${id}/events`,
  }),

  // Shopify
  SHOPIFY: Object.freeze({
    STORE: `${V1}/shopify/store`,
    CONNECTION_STATUS: `${V1}/shopify/connection-status`,
    CONNECTION: `${V1}/shopify/connection`,
    CONNECT: `${V1}/shopify/connect`,
    DISCONNECT: `${V1}/shopify/disconnect`,
    REAUTHORIZE: `${V1}/shopify/reauthorize`,
    TEST_CONNECTION: `${V1}/shopify/test-connection`,
    HEALTH: `${V1}/shopify/health`,
    TEMPLATES: `${V1}/shopify/templates`,
    PRODUCTS: `${V1}/shopify/products`,
    PRODUCTS_MY: `${V1}/shopify/products/my-products`,
    PRODUCTS_FROM_TEMPLATE: `${V1}/shopify/products/from-template`,
    PRODUCTS_BULK_CREATE: `${V1}/shopify/products/bulk-create`,
    PRODUCTS_BULK_DELETE: `${V1}/shopify/products/bulk-delete`,
    // Edit one product (PATCH semantics: omitted fields are left alone).
    PRODUCT: id => `${V1}/shopify/products/${id}`,
    // Apply one change set across many products. tag_mode add|remove edits each
    // product's existing tags instead of flattening them all to one list.
    PRODUCTS_BULK_UPDATE: `${V1}/shopify/products/bulk-update`,
    // Re-render a product's images with a different mockup, REPLACING them.
    PRODUCT_REMOCKUP: id => `${V1}/shopify/products/${id}/remockup`,
    PRODUCTS_BULK_REMOCKUP: `${V1}/shopify/products/bulk-remockup`,
    // Async chunked bulk-create (fire-and-forget)
    UPLOAD_BATCH_INIT: `${V1}/shopify/products/upload-batches/init`,
    UPLOAD_BATCH_CHUNK: jobId => `${V1}/shopify/products/upload-batches/${jobId}/chunk`,
    UPLOAD_BATCH_FINALIZE: jobId => `${V1}/shopify/products/upload-batches/${jobId}/finalize`,
    UPLOAD_JOBS_LIST: `${V1}/shopify/products/upload-batches/jobs`,
    ORDERS: `${V1}/shopify/orders`,
    ORDERS_STATS: `${V1}/shopify/orders/stats`,
    ORDERS_TOP_PRODUCTS: `${V1}/shopify/orders/top-products`,
    // Loads HISTORICAL orders into shopify_cached_orders, which every Shopify
    // analytics view reads. Without it the table only ever fills from the
    // orders/paid webhook — i.e. orders paid after the webhook was wired up.
    ORDERS_BACKFILL: `${V1}/shopify/orders/backfill`,
    ANALYTICS_SUMMARY: `${V1}/shopify/analytics/summary`,
    ANALYTICS_DETAILED: `${V1}/shopify/analytics/detailed`,
    // Profit/revenue per day-week-month-year over a date range + best-selling
    // products and categories (shared cross-platform analytics contract).
    ANALYTICS_SALES: `${V1}/shopify/analytics/sales`,
    // Per-item COGS + profit grouped by product type and by vendor
    // (Shopify cost-breakdown tab). Growth+ gated.
    ANALYTICS_COST_BREAKDOWN: `${V1}/shopify/analytics/cost-breakdown`,
    ORDER_PRINT_FILE: orderId => `${V1}/shopify/orders/${orderId}/print-file`,
    PRINT_FILES: `${V1}/shopify/print-files`,
    PRINT_FILES_FROM_SELECTION: `${V1}/shopify/print-files-from-selection`,
    PRINT_FILES_BY_PRINTER_METHOD: `${V1}/shopify/print-files-by-printer-method`,
    // ODP (OnDemandPrintfiles) — pushes selected Shopify orders' line items to
    // ODP's push-intake feed instead of rendering a zip. Sibling to
    // ORDERS.ODP_PUSH (Etsy); status is shared across platforms via
    // ORDERS.ODP_STATUS (one OdpConnection row per seller).
    ODP_PUSH: `${V1}/shopify/odp/push-selected-orders`,
    METADATA_PRODUCT_TYPES: `${V1}/shopify/metadata/product-types`,
    METADATA_VENDORS: `${V1}/shopify/metadata/vendors`,
    METADATA_TAGS: `${V1}/shopify/metadata/tags`,
    METADATA_THEMES: `${V1}/shopify/metadata/themes`,
    METADATA_THEME_TEMPLATES: `${V1}/shopify/metadata/theme-templates`,
    METADATA_COLLECTIONS: `${V1}/shopify/metadata/collections`,
    METADATA_TAXONOMY_CATEGORIES: `${V1}/shopify/metadata/taxonomy-categories`,
    METADATA_LOCATIONS: `${V1}/shopify/metadata/locations`,
  }),

  // AI SEO (platform-agnostic)
  AI_SEO: Object.freeze({
    GENERATE: `${V1}/ai-seo/generate`,
  }),

  // AI Metafields (Shopify-targeted, but platform-agnostic API)
  AI_METAFIELDS: Object.freeze({
    GENERATE: `${V1}/ai-metafields/generate`,
    GENERATE_STANDARD: `${V1}/ai-metafields/generate-standard`,
  }),

  // E-commerce (CraftFlow)
  ECOMMERCE: Object.freeze({
    PRODUCTS: `${V1}/ecommerce/admin/products/`,
    PRODUCT_DUPLICATES: `${V1}/ecommerce/admin/products/duplicates`,
    PRODUCT_DUPLICATES_RETIRE: `${V1}/ecommerce/admin/products/duplicates/retire`,
    PRODUCT_RECOVERY_ORPHANS: `${V1}/ecommerce/admin/products/recovery/orphans`,
    PRODUCT_RECOVERY_DELETED: `${V1}/ecommerce/admin/products/recovery/deleted-products`,
    PRODUCT_RECOVERY_RESTORE: `${V1}/ecommerce/admin/products/recovery/restore`,
    PRODUCT_RECOVERY_PREVIEW: `${V1}/ecommerce/admin/products/recovery/preview`,
    PRODUCT_RECOVERY_ASSIGN: `${V1}/ecommerce/admin/products/recovery/assign`,
    PRODUCT_RECOVERY_DESIGNLESS: `${V1}/ecommerce/admin/products/recovery/products-without-design`,
    TRADEMARK_NAMES_SCAN: `${V1}/ecommerce/admin/products/trademark-names/scan`,
    TRADEMARK_NAMES_PROPOSE: `${V1}/ecommerce/admin/products/trademark-names/propose`,
    TRADEMARK_NAMES_APPLY: `${V1}/ecommerce/admin/products/trademark-names/apply`,
    AI_RENAME_PROPOSE: `${V1}/ecommerce/admin/products/ai-rename/propose`,
    AI_RENAME_APPLY: `${V1}/ecommerce/admin/products/ai-rename/apply`,
    PRODUCTS_ALL_IDS: `${V1}/ecommerce/admin/products/ids`, // (id, name) for every product matching filters
    PRODUCT_PACKS: `${V1}/ecommerce/admin/products`, // append /{id}/packs
    PRODUCT_MOCKUPS: `${V1}/ecommerce/admin/products/generate-mockups`,
    PRODUCT_MOCKUPS_DISCARD: `${V1}/ecommerce/admin/products/generate-mockups/discard`,
    PRODUCT_DESIGN_FILE: `${V1}/ecommerce/admin/products/design-file`,
    LINKED_ETSY_IDS: `${V1}/ecommerce/admin/products/linked-etsy-ids`,
    LINKED_DESIGN_IDS: `${V1}/ecommerce/admin/products/linked-design-ids`,
    IMPORT_FROM_DESIGNS: `${V1}/ecommerce/admin/products/import-from-designs`,
    IMPORT_FROM_DESIGNS_ASYNC: `${V1}/ecommerce/admin/products/import-from-designs-async`,
    IMPORT_JOB: id => `${V1}/ecommerce/admin/products/import-jobs/${id}`,
    IMPORT_JOBS: `${V1}/ecommerce/admin/products/import-jobs`,
    // Async bulk-template product upload (chunked, fire-and-forget).
    PRODUCT_UPLOAD_BATCH_INIT: `${V1}/ecommerce/admin/products/upload-batches/init`,
    PRODUCT_UPLOAD_BATCH_CHUNK: jobId => `${V1}/ecommerce/admin/products/upload-batches/${jobId}/chunk`,
    PRODUCT_UPLOAD_BATCH_FINALIZE: jobId => `${V1}/ecommerce/admin/products/upload-batches/${jobId}/finalize`,
    PRODUCT_JOBS: `${V1}/ecommerce/admin/products/product-jobs`,
    PRODUCT_JOB: id => `${V1}/ecommerce/admin/products/product-jobs/${id}`,
    ORDERS: `${V1}/ecommerce/admin/orders/`,
    ORDER_PARCEL_ESTIMATE: id => `${V1}/ecommerce/admin/orders/${id}/parcel-estimate`,
    // Records a tracking number for a label bought OUTSIDE CraftFlow — the
    // endpoint also fulfils the order and emails the customer.
    ORDER_TRACKING: id => `${V1}/ecommerce/admin/orders/${id}/tracking`,
    ORDERS_BULK_STATUS: `${V1}/ecommerce/admin/orders/bulk-status`,
    ORDERS_PRINT_FILES: `${V1}/ecommerce/admin/orders/print-files-from-selection`,
    ORDERS_PRINT_BY_PRINTER_METHOD: `${V1}/ecommerce/admin/orders/print-files-by-printer-method`,
    // ODP (OnDemandPrintfiles) — pushes selected CraftFlow-native orders' line
    // items to ODP's push-intake feed instead of rendering a zip. Sibling to
    // ORDERS.ODP_PUSH (Etsy) and SHOPIFY.ODP_PUSH; status is shared across
    // platforms via ORDERS.ODP_STATUS (one OdpConnection row per seller).
    ORDERS_ODP_PUSH: `${V1}/ecommerce/admin/orders/odp/push-selected-orders`,
    ORDERS_PACKING_SLIPS: `${V1}/ecommerce/admin/orders/packing-slips`,
    ORDERS_BATCH_LABELS: `${V1}/ecommerce/admin/orders/batch-labels`,
    ORDERS_BATCH_RATE_OPTIONS: `${V1}/ecommerce/admin/orders/batch-labels/rate-options`,
    CUSTOMERS: `${V1}/ecommerce/admin/customers/`,
    CUSTOMER_DETAIL: id => `${V1}/ecommerce/admin/customers/${id}`,
    STOREFRONT_SETTINGS: `${V1}/ecommerce/admin/storefront-settings/`,
    SENDER_DOMAIN: `${V1}/ecommerce/admin/storefront-settings/sender-domain`,
    SENDER_DOMAIN_VERIFY: `${V1}/ecommerce/admin/storefront-settings/sender-domain/verify`,
    SUBDOMAIN_SENDER: `${V1}/ecommerce/admin/storefront-settings/subdomain-sender`,
    SUBDOMAIN_SENDER_VERIFY: `${V1}/ecommerce/admin/storefront-settings/subdomain-sender/verify`,
    MEDIA_LIBRARY: `${V1}/ecommerce/admin/media-library/`,
    MEDIA_LIBRARY_UPLOAD: `${V1}/ecommerce/admin/media-library/upload`,
    MEDIA_LIBRARY_DELETE: id => `${V1}/ecommerce/admin/media-library/${id}`,

    // Marketing assets — promotional images published for use OUTSIDE the app.
    // Distinct from MEDIA_LIBRARY, whose URLs are only ever rendered inside the
    // storefront. Publishing mints a token served at /m/{token}; revoking kills
    // that link permanently. Archive is reversible and does NOT touch the link.
    MARKETING_ASSETS: `${V1}/ecommerce/admin/marketing-assets/`,
    MARKETING_ASSETS_UPLOAD: `${V1}/ecommerce/admin/marketing-assets/upload`,
    MARKETING_ASSET_PUBLISH: id => `${V1}/ecommerce/admin/marketing-assets/${id}/publish`,
    MARKETING_ASSET_REVOKE: id => `${V1}/ecommerce/admin/marketing-assets/${id}/revoke`,
    MARKETING_ASSET_ARCHIVE: (id, unarchive = false) =>
      `${V1}/ecommerce/admin/marketing-assets/${id}/archive${unarchive ? '?unarchive=true' : ''}`,
    MARKETING_ASSET_DELETE: id => `${V1}/ecommerce/admin/marketing-assets/${id}`,
    // Which saved email templates still name this asset. A FLOOR, never a total —
    // it cannot see email already sent or a link pasted outside the app.
    MARKETING_ASSET_USAGE: id => `${V1}/ecommerce/admin/marketing-assets/${id}/usage`,
    STOREFRONT_SETTINGS_DRAFT: `${V1}/ecommerce/admin/storefront-settings/draft`,
    STOREFRONT_SETTINGS_PUBLISH: `${V1}/ecommerce/admin/storefront-settings/publish`,
    STOREFRONT_SETTINGS_DISCARD: `${V1}/ecommerce/admin/storefront-settings/discard-draft`,
    STOREFRONT_LOGO: `${V1}/ecommerce/admin/storefront-settings/logo`,
    STOREFRONT_FAVICON: `${V1}/ecommerce/admin/storefront-settings/favicon`,
    // POST — re-render every page of the caller's storefront. 429 within a minute of the
    // last one; 503 while page-cache revalidation is off on this environment.
    STOREFRONT_CACHE_PURGE: `${V1}/ecommerce/admin/storefront-cache/purge`,
    // GET — {enabled, cooldown_s}: the page refresh card shows only while enabled.
    STOREFRONT_CACHE_CONFIG: `${V1}/ecommerce/admin/storefront-cache/config`,
    SHIPPING_PACKINGS: `${V1}/ecommerce/admin/shipping/packings/`,
    DOMAIN_STATUS: `${V1}/ecommerce/storefront/domain/status`,
    DOMAIN_SUBDOMAIN: `${V1}/ecommerce/storefront/domain/subdomain`,
    DOMAIN_DOMAIN: `${V1}/ecommerce/storefront/domain/domain`,
    DOMAIN_INSTRUCTIONS: `${V1}/ecommerce/storefront/domain/domain/instructions`,
    DOMAIN_VERIFY: `${V1}/ecommerce/storefront/domain/domain/request-verification`,
    DOMAIN_PUBLISH: `${V1}/ecommerce/storefront/domain/publish`,
    DOMAIN_MAINTENANCE: `${V1}/ecommerce/storefront/domain/maintenance`,
    DOMAIN_PASSWORD: `${V1}/ecommerce/storefront/domain/password`,
    // POST {host: string | null} — null clears the pin and goes back to automatic.
    DOMAIN_PRIMARY_HOST: `${V1}/ecommerce/storefront/domain/primary-host`,
    DOMAIN_ADMIN_PENDING: `${V1}/ecommerce/storefront/domain/admin/pending`,
    DOMAIN_ADMIN_VERIFY: `${V1}/ecommerce/storefront/domain/admin/verify`,
    DOMAIN_ADMIN_REJECT: `${V1}/ecommerce/storefront/domain/admin/reject`,
    // THE email-type manifest (server/src/services/email_types.py). The editor's
    // type picker, its variable cheat-sheet and both list filters read it instead
    // of each keeping their own hand-written subset of the same list.
    EMAILS_TYPES: `${V1}/ecommerce/admin/emails/types`,
    EMAILS_TEMPLATES: `${V1}/ecommerce/admin/emails/templates`,
    EMAILS_TEMPLATE_PREVIEW: `${V1}/ecommerce/admin/emails/templates/preview`,
    EMAILS_TEMPLATE_SEND_TEST: id => `${V1}/ecommerce/admin/emails/templates/${id}/send-test`,
    EMAILS_TEMPLATE_GENERATE_HTML: `${V1}/ecommerce/admin/emails/templates/generate-html`,
    EMAILS_TEMPLATE_PRESETS: `${V1}/ecommerce/admin/emails/templates/presets`,
    EMAILS_LOGS: `${V1}/ecommerce/admin/emails/logs`,
    EMAILS_SUBSCRIBERS: `${V1}/ecommerce/admin/emails/subscribers`,
    EMAILS_SUBSCRIBERS_EXPORT: `${V1}/ecommerce/admin/emails/subscribers/export`,
    EMAILS_SCHEDULED: `${V1}/ecommerce/admin/emails/scheduled`,
    EMAILS_ANALYTICS_SUMMARY: `${V1}/ecommerce/admin/emails/analytics/summary`,
    EMAILS_SEND_MARKETING: `${V1}/ecommerce/admin/emails/send-marketing`,
    STOREFRONT_PAGES: `${V1}/ecommerce/admin/pages/`,
    STOREFRONT_PAGE_IMAGE_UPLOAD: `${V1}/ecommerce/admin/pages/upload-image`,
    STOREFRONT_PAGES_SEED: `${V1}/ecommerce/admin/pages/seed-defaults`,
    STOREFRONT_SECTION_MANIFEST: `${V1}/ecommerce/admin/pages/section-manifest`,
    STOREFRONT_PAGE_PUBLISH: id => `${V1}/ecommerce/admin/pages/${id}/publish`,
    STOREFRONT_PAGE_DISCARD: id => `${V1}/ecommerce/admin/pages/${id}/discard-draft`,
    STOREFRONT_PAGE_TEMPLATES: `${V1}/ecommerce/admin/pages/templates/list`,
    STOREFRONT_PAGE_FROM_TEMPLATE: `${V1}/ecommerce/admin/pages/from-template`,
    STOREFRONT_PAGE_DUPLICATE: id => `${V1}/ecommerce/admin/pages/${id}/duplicate`,
    STOREFRONT_PAGE_SCHEDULE: id => `${V1}/ecommerce/admin/pages/${id}/schedule-publish`,
    STOREFRONT_PAGE_CANCEL_SCHEDULE: id => `${V1}/ecommerce/admin/pages/${id}/cancel-schedule`,
    STOREFRONT_PAGE_VERSIONS: id => `${V1}/ecommerce/admin/pages/${id}/versions`,
    STOREFRONT_PAGE_RESTORE_VERSION: (id, vid) => `${V1}/ecommerce/admin/pages/${id}/versions/${vid}/restore`,
    CATEGORIES: `${V1}/ecommerce/admin/categories/`,
    CATEGORIES_SEED: `${V1}/ecommerce/admin/categories/seed-defaults`,
    // NOTE: CATEGORIES ends in '/', so building `${CATEGORIES}/${id}` produces a
    // double-slash URL that 404s on the backend — always use CATEGORY_DETAIL.
    CATEGORY_DETAIL: id => `${V1}/ecommerce/admin/categories/${id}`,
    COLLECTIONS: `${V1}/ecommerce/admin/collections/`,
    COLLECTION_DETAIL: id => `${V1}/ecommerce/admin/collections/${id}`,
    COLLECTION_PRODUCTS: id => `${V1}/ecommerce/admin/collections/${id}/products`,
    COLLECTION_RULE_METADATA: `${V1}/ecommerce/admin/collections/rule-metadata`,
    COLLECTION_RULE_FIELD_VALUES: field =>
      `${V1}/ecommerce/admin/collections/rule-field-values?field=${encodeURIComponent(field)}`,
    COLLECTION_PREVIEW_RULES: `${V1}/ecommerce/admin/collections/preview-rules`,
    TAXONOMY: kind => `${V1}/ecommerce/admin/taxonomy/${kind}`,
    TAXONOMY_DETAIL: (kind, termId) => `${V1}/ecommerce/admin/taxonomy/${kind}/${termId}`,
    TAXONOMY_SEED: kind => `${V1}/ecommerce/admin/taxonomy/${kind}/seed-defaults`,
    PRODUCTS_BULK_STATUS: `${V1}/ecommerce/admin/products/bulk-status`,
    PRODUCTS_BULK_UPDATE: `${V1}/ecommerce/admin/products/bulk-update`,
    // Push a CraftFlow template's current values onto the products made from it.
    TEMPLATE_PUSH_PREVIEW: id => `${V1}/ecommerce/admin/template-push/${id}/preview`,
    TEMPLATE_PUSH_APPLY: id => `${V1}/ecommerce/admin/template-push/${id}/apply`,
    // Point each selected product at a DIFFERENT one of its own mockups. A
    // separate action from BULK_UPDATE, which applies one shared VALUE to every
    // product — this computes a different value per product, and has a third
    // outcome (a one-mockup product is skipped, not failed).
    PRODUCTS_BULK_REROLL_MOCKUP: `${V1}/ecommerce/admin/products/bulk-reroll-featured-image`,
    LOW_STOCK: `${V1}/ecommerce/admin/products/low-stock`,
  }),

  // Analytics (CraftFlow Commerce)
  ANALYTICS: Object.freeze({
    OVERVIEW: `${V1}/ecommerce/admin/analytics/overview`,
    PRODUCTS: `${V1}/ecommerce/admin/analytics/products`,
    // Profit/revenue per day-week-month-year over a date range + best-selling
    // products and categories (shared cross-platform analytics contract).
    SALES: `${V1}/ecommerce/admin/analytics/sales`,
    // Per-item COGS + profit grouped by product template and by category
    // (CraftFlow cost-breakdown tab). Growth+ gated.
    COST_BREAKDOWN: `${V1}/ecommerce/admin/analytics/cost-breakdown`,
    // Cohort behaviour of the customers a window acquired: repeat rate, reorder
    // cadence, revenue per new customer, RFM segments, and the derived max_cac /
    // reorder_trigger_day constants. Counts GUEST orders (identity is the
    // buyer's email, not customer_id), so its customer counts are deliberately
    // higher than OVERVIEW's. Growth+ gated.
    CUSTOMERS: `${V1}/ecommerce/admin/analytics/customers`,
    // First-party storefront traffic — acquisition + behavior + funnel over a
    // date range (served DB-first from the ecommerce_web_daily rollup). EXPORT
    // streams the same payload as a single CSV (summary + breakdowns + funnel).
    TRAFFIC: `${V1}/ecommerce/admin/analytics/traffic`,
    TRAFFIC_EXPORT: `${V1}/ecommerce/admin/analytics/traffic/export.csv`,
  }),

  // Reviews (CraftFlow Commerce)
  REVIEWS: Object.freeze({
    LIST: `${V1}/ecommerce/admin/reviews/`,
    DETAIL: id => `${V1}/ecommerce/admin/reviews/${id}`,
    STATUS: id => `${V1}/ecommerce/admin/reviews/${id}/status`,
    BULK_STATUS: `${V1}/ecommerce/admin/reviews/bulk-status`,
    BULK_DELETE: `${V1}/ecommerce/admin/reviews/bulk-delete`,
  }),

  // Customer Messages (CraftFlow Commerce)
  MESSAGES: Object.freeze({
    LIST: `${V1}/ecommerce/admin/messages/`,
    DETAIL: id => `${V1}/ecommerce/admin/messages/${id}`,
    REPLY: id => `${V1}/ecommerce/admin/messages/${id}/reply`,
    STATUS: id => `${V1}/ecommerce/admin/messages/${id}/status`,
    UNREAD_COUNT: `${V1}/ecommerce/admin/messages/unread-count`,
  }),

  // Discounts
  DISCOUNTS: Object.freeze({
    LIST: `${V1}/ecommerce/admin/discounts/`,
    DETAIL: id => `${V1}/ecommerce/admin/discounts/${id}`,
  }),

  // Scheduled sales (the price rule + its schedule). PREVIEW_SCHEDULE runs the
  // SERVER's occurrence rule against an unsaved payload, so the authoring form
  // never needs a copy of it.
  SALES: Object.freeze({
    LIST: `${V1}/ecommerce/admin/sales/`,
    DETAIL: id => `${V1}/ecommerce/admin/sales/${id}`,
    PREVIEW_SCHEDULE: `${V1}/ecommerce/admin/sales/preview-schedule`,
  }),

  // Wholesale price lists: a schedule of volume bands attached to a customer
  // group. `Customer.customer_group` is the key — it has been on the customer
  // record and admin-editable all along, so tagging a customer "wholesale"
  // starts meaning something the moment a list names that group.
  PRICE_LISTS: Object.freeze({
    LIST: `${V1}/ecommerce/admin/price-lists/`,
    DETAIL: id => `${V1}/ecommerce/admin/price-lists/${id}`,
    BANDS: id => `${V1}/ecommerce/admin/price-lists/${id}/bands`,
    BAND: (id, bandId) => `${V1}/ecommerce/admin/price-lists/${id}/bands/${bandId}`,
  }),

  // Wholesale prospects: the outreach list — businesses the seller is trying to
  // open a trade account with. NOT customers; nobody here has bought anything.
  WHOLESALE_PROSPECTS: Object.freeze({
    LIST: `${V1}/ecommerce/admin/wholesale-prospects/`,
    DETAIL: id => `${V1}/ecommerce/admin/wholesale-prospects/${id}`,
    TOUCH: id => `${V1}/ecommerce/admin/wholesale-prospects/${id}/touch`,
  }),

  // Gift cards (stored-value; admin issues + shares a redeem link)
  GIFT_CARDS: Object.freeze({
    LIST: `${V1}/ecommerce/admin/gift-cards/`,
    DETAIL: id => `${V1}/ecommerce/admin/gift-cards/${id}`,
  }),

  // Seller-issued customer invoices — a bill for work the catalogue cannot
  // express. NOT the platform's own Stripe subscription invoices (those bill the
  // seller for CraftFlow and live under SUBSCRIPTIONS); the `ecommerce/` segment
  // is what keeps the two apart.
  INVOICES: Object.freeze({
    LIST: `${V1}/ecommerce/admin/invoices/`,
    DETAIL: id => `${V1}/ecommerce/admin/invoices/${id}`,
    SEND: id => `${V1}/ecommerce/admin/invoices/${id}/send`,
    VOID: id => `${V1}/ecommerce/admin/invoices/${id}/void`,
    REVISE: id => `${V1}/ecommerce/admin/invoices/${id}/revise`,
    // The pay link of a SENT invoice, for the seller to copy; sends nothing.
    PAY_LINK: id => `${V1}/ecommerce/admin/invoices/${id}/pay-link`,
    // POST, no body: copies an invoice at ANY status into a fresh draft ({invoice}, 201). Sends nothing.
    DUPLICATE: id => `${V1}/ecommerce/admin/invoices/${id}/duplicate`,
    // One collection against a bill, not the bill — a deposit is taken before any
    // order exists, so there is no order refund route that could stand in for this.
    REFUND_PAYMENT: (id, paymentId) => `${V1}/ecommerce/admin/invoices/${id}/payments/${paymentId}/refund`,
    INTERNAL_NOTE: id => `${V1}/ecommerce/admin/invoices/${id}/internal-note`,
    // The builder's "add from your products" feed: `?q=&page=`, 25 native products a page, list prices only.
    CATALOG_ITEMS: `${V1}/ecommerce/admin/invoice-catalog/items`,
  }),

  // Shipping Rules
  SHIPPING_RULES: Object.freeze({
    LIST: `${V1}/ecommerce/admin/shipping-rules/`,
    DETAIL: id => `${V1}/ecommerce/admin/shipping-rules/${id}`,
  }),

  // Tax Configuration
  TAX: Object.freeze({
    CONFIG: `${V1}/ecommerce/admin/tax/config`,
    OVERRIDES: `${V1}/ecommerce/admin/tax/overrides/`,
    OVERRIDE_DETAIL: id => `${V1}/ecommerce/admin/tax/overrides/${id}`,
  }),

  // URL Redirects
  REDIRECTS: Object.freeze({
    LIST: `${V1}/ecommerce/admin/redirects/`,
    DETAIL: id => `${V1}/ecommerce/admin/redirects/${id}`,
  }),

  // Builder Config (Gang Sheet Builder Settings)
  BUILDER_CONFIG: Object.freeze({
    ADMIN: `${V1}/ecommerce/admin/builder-config/`,
    SHIPPING_PREVIEW: `${V1}/ecommerce/admin/builder-config/shipping-preview`,
  }),

  // Builder (Gang Sheet Sessions)
  BUILDER: Object.freeze({
    SESSIONS: `${V1}/ecommerce/admin/builder-sessions`,
    SESSION_DETAIL: id => `${V1}/ecommerce/admin/builder-sessions/${id}`,
    SESSION_EXPORT: id => `${V1}/ecommerce/admin/builder-sessions/${id}/export`,
    SESSION_DOWNLOAD: id => `${V1}/ecommerce/admin/builder-sessions/${id}/download`,
    SESSION_PREVIEW: id => `${V1}/ecommerce/admin/builder-sessions/${id}/preview`,
    SESSION_IMAGE: (sessionId, imageId) => `${V1}/ecommerce/admin/builder-sessions/${sessionId}/images/${imageId}`,
    UPDATE_STATUS: id => `${V1}/ecommerce/admin/builder-sessions/${id}/status`,
  }),

  // Gallery
  GALLERY: Object.freeze({
    ASSETS: `${V1}/ecommerce/admin/gallery`,
    ASSET_DETAIL: id => `${V1}/ecommerce/admin/gallery/${id}`,
    UPLOAD: `${V1}/ecommerce/admin/gallery/upload`,
    BATCH_DELETE: `${V1}/ecommerce/admin/gallery/batch-delete`,
  }),

  // Export Jobs
  EXPORT_JOBS: Object.freeze({
    LIST: `${V1}/ecommerce/admin/export-jobs/`,
    DETAIL: id => `${V1}/ecommerce/admin/export-jobs/${id}`,
    BATCH: `${V1}/ecommerce/admin/export-jobs/batch`,
  }),

  // Subscriptions
  SUBSCRIPTIONS: Object.freeze({
    BASE: `${V1}/subscriptions`,
    CURRENT: `${V1}/subscriptions/current`,
    CREATE_CHECKOUT: `${V1}/subscriptions/checkout`,
    CANCEL: `${V1}/subscriptions/cancel`,
    UPDATE: `${V1}/subscriptions/update`,
    BILLING_HISTORY: `${V1}/subscriptions/billing-history`,
    CUSTOMER_PORTAL: `${V1}/subscriptions/customer-portal`,
    MANAGE: `${V1}/subscriptions/create-portal-session`,
    USAGE: `${V1}/subscriptions/usage`,
    ENTITLEMENTS: `${V1}/subscriptions/entitlements`,
    FEATURES: `${V1}/subscriptions/features`,
    TIERS: `${V1}/subscriptions/tiers`,
    CHECK_FEATURE: `${V1}/subscriptions/check-feature`,
    INCREMENT_USAGE: `${V1}/subscriptions/increment-usage`,
    WEBHOOK: `${V1}/subscriptions/webhook`,
  }),

  // Printers
  PRINTERS: Object.freeze({
    LIST: `${V1}/printers/`,
    TEMPLATES: `${V1}/printers`, // append /{id}/templates
  }),

  // OAuth tokens
  OAUTH: Object.freeze({
    TOKENS: `${V1}/oauth/tokens`,
    SERVICE_REFRESH_ALL: `${V1}/oauth/service/refresh-all`,
    SERVICE_STATUS: `${V1}/oauth/service/status`,
  }),

  // Templates (product templates)
  TEMPLATES: Object.freeze({
    LIST: `${V1}/templates/`,
  }),

  // Shopify Migration
  SHOPIFY_MIGRATION: Object.freeze({
    TRIGGER_IMPORT: storeId => `${V1}/shopify/stores/${storeId}/import`,
    IMPORT_STATUS: storeId => `${V1}/shopify/stores/${storeId}/import/status`,
    EXCEPTIONS: storeId => `${V1}/shopify/stores/${storeId}/import/exceptions`,
    ASSIGN_SPEC: (storeId, productId) => `${V1}/shopify/stores/${storeId}/products/${productId}/spec`,
    MIGRATED_DESIGNS: storeId => `${V1}/shopify/stores/${storeId}/migrated-designs`,
  }),

  // Admin System
  ADMIN_SYSTEM: Object.freeze({
    OVERVIEW: `${V1}/admin/system/overview`,
    USERS: `${V1}/admin/users/`,
    USER: id => `${V1}/admin/users/${id}`,
  }),

  // Admin — Etsy quota observability + reset escape hatch.
  // RESET clears today's Redis counter so we self-heal from a stuck
  // synthetic lock without waiting for the cooldown TTL.
  ADMIN_ETSY_QUOTA: Object.freeze({
    USAGE: `${V1}/admin/etsy-quota/usage`,
    RESET: `${V1}/admin/etsy-quota/reset`,
    CALLS: userId => `${V1}/admin/etsy-quota/calls/${userId}`,
  }),

  // Admin — Shopify products with no listing→design map row. Read-only;
  // repair is scripts/backfill_shopify_design_map.py (#796).
  ADMIN_SHOPIFY_DESIGNS: Object.freeze({
    UNMAPPED: `${V1}/admin/shopify-designs/unmapped`,
  }),

  // Admin — real-user speed (Core Web Vitals) across every shop, from the
  // nightly storefront_web_vitals_daily rollup. OVERVIEW is one UTC day's
  // shop-wide p75s (?day=, ?device=); SHOP is one shop per route template.
  ADMIN_WEB_VITALS: Object.freeze({
    OVERVIEW: `${V1}/admin/web-vitals`,
    SHOP: userId => `${V1}/admin/web-vitals/${userId}`,
  }),

  // Admin — design remap tool. Lets an operator rename a design file
  // on NAS + update DB rows + re-link the etsy_listing_design_map row
  // so a listing points at the correct design. Used when a shop's
  // mockups/listings drifted out of sync with its source design files.
  ADMIN_DESIGN_REMAP: Object.freeze({
    SHOPS: `${V1}/admin/design-remap/shops`,
    SHOP: userId => `${V1}/admin/design-remap/shop/${userId}`,
    NAS_SNAPSHOT: userId => `${V1}/admin/design-remap/shop/${userId}/nas-snapshot`,
    REFRESH_LISTINGS: userId => `${V1}/admin/design-remap/shop/${userId}/refresh-listings`,
    APPLY: `${V1}/admin/design-remap/apply`,
    IMPORT_LINK: `${V1}/admin/design-remap/import-link`,
    HISTORY: userId => `${V1}/admin/design-remap/history/${userId}`,

    // R2 gallery remap — repair design_images rows with a NULL r2_key by
    // letting an operator LOOK at the shop's R2 objects and pick the right
    // one. Perceptual hashing can't settle this (imagehash fingerprints the
    // luma plane, and a DTF design's artwork lives in its alpha channel), so
    // the human's eyes are the evidence and this is a manual, visual flow.
    //   UNMAPPED   — GET, every design_images row with NO marker (r2_key NULL
    //                *or* blank — link-r2 treats both as unmapped, so a
    //                narrower filter would hide rows it can repair). Returns
    //                {designs, total, truncated}: the list is capped, and a
    //                bare array could not tell a full answer from a partial.
    //   R2_GALLERY — GET, paginated ListObjectsV2 scoped to one shop's
    //                prefix. Query: user_id (required), cursor, limit, q.
    //   R2_OBJECT  — GET, streams one object's bytes. Query: key (required),
    //                thumb=true for the downscaled preview. Admin-gated, so
    //                it must be fetched with the JWT (a bare <img src> can't
    //                carry it) — the panel loads it via api.fetchFile.
    //   LINK_R2    — POST {design_id, r2_key, force, allow_cross_shop,
    //                allow_reclaim}; HEADs the key, writes the r2_key marker
    //                + the four full-size perceptual hashes, and audits.
    //                Never touches file_path. The three booleans are separate
    //                on purpose — overwriting your own marker, reaching into
    //                another tenant's prefix, and pointing two rows at one
    //                object are three different mistakes, and each 409 names
    //                the flag that authorises it.
    // Ranked design shortlist for ONE listing. Query: user_id, listing_id,
    // limit (default 20, capped server-side). Returns an ENVELOPE, never a bare
    // array — {has_exact_match, listing_nnnn, exact_count, warning, candidates}
    // — because a bare list cannot tell "here is the design" from "here are the
    // nearest numbers, none of which is it". Each row carries match_kind
    // ("exact" = NNNN boundary match | "proximity" = nearest by number, NOT a
    // match) and already_linked_to, so the UI can refuse to call a neighbour a
    // match and can warn before a pick steals another listing's mapping.
    CANDIDATES: `${V1}/admin/design-remap/candidates`,
    R2_UNMAPPED: `${V1}/admin/design-remap/unmapped`,
    R2_GALLERY: `${V1}/admin/design-remap/r2-gallery`,
    R2_OBJECT: `${V1}/admin/design-remap/r2-object`,
    LINK_R2: `${V1}/admin/design-remap/link-r2`,
  }),

  // Admin — cross-user upload-job observability. LIST returns the
  // recent design + Etsy jobs across every seller; SUMMARY is a
  // GROUP BY status counter for header tiles.
  ADMIN_UPLOAD_JOBS: Object.freeze({
    LIST: `${V1}/admin/upload-jobs`,
    SUMMARY: `${V1}/admin/upload-jobs/summary`,
    // POST — admin escape hatch for a stuck/failed job. The path is
    // /{kind}/{id}/retry; the panel composes it inline.
    RETRY: (kind, jobId) => `${V1}/admin/upload-jobs/${kind}/${jobId}/retry`,
  }),

  // A shop's own carrier credentials. The key is WRITE-ONLY — GET returns
  // `has_api_key` + `api_key_last4` and never the key itself.
  SHIPPING_SETTINGS: Object.freeze({
    // One key for read and write — the HTTP verb is what distinguishes them,
    // and two constants holding the same string is what the duplicate-value
    // guard in this file's test exists to catch.
    SETTINGS: `${V1}/ecommerce/admin/shipping/settings`,
    VERIFY: `${V1}/ecommerce/admin/shipping/verify`,
    DELETE_KEY: `${V1}/ecommerce/admin/shipping/key`,
  }),

  // Admin — the 15 system emails CraftFlow sends its SELLERS (welcome, payment
  // receipt, trial reminder, ...). Super-admin only. Editing one changes what
  // every shop owner receives; a seller cannot override these, which is why
  // they no longer appear in the seller email editor's type picker.
  ADMIN_SYSTEM_EMAILS: Object.freeze({
    LIST: `${V1}/admin/system-emails`,
    DETAIL: emailType => `${V1}/admin/system-emails/${emailType}`,
    PREVIEW: emailType => `${V1}/admin/system-emails/${emailType}/preview`,
    TEST_SEND: emailType => `${V1}/admin/system-emails/${emailType}/test-send`,
  }),

  // Admin — Send To PrintMade. A seller's printfile, copied to R2 once per seller
  // per day per platform and kept 30 days. DOWNLOAD is a POST that mints a
  // short-lived signed R2 link on click: never a stream through the API (the
  // edge's ~300s cap), and never stored, so a link can't go stale in the list.
  // SELLER is the super admin's per-seller switch.
  ADMIN_PRINTMADE: Object.freeze({
    SUBMISSIONS: `${V1}/admin/printmade/submissions`,
    DOWNLOAD: id => `${V1}/admin/printmade/submissions/${id}/download`,
    SELLER: userId => `${V1}/admin/printmade/sellers/${userId}`,
  }),

  // Admin — ODP (OnDemandPrintfiles). A separate, seller-opt-in print-
  // fulfillment vendor from Send To PrintMade: instead of CraftFlow rendering
  // a zip, ODP does its own layout/rendering from pushed order-line data (a
  // presigned design URL, product, qty). SELLERS lists every seller + their
  // connection state; SELLER is the super admin's per-seller switch, label,
  // (PUT `{enabled?, feed_source_label?}`); the list never carries the
  // intake key, only `intake_key_last4`. REVEAL decrypts one seller's key (audited, no-store). SUBMISSIONS is recent pushes across
  // every seller, for admin visibility.
  ADMIN_ODP: Object.freeze({
    SELLERS: `${V1}/admin/odp/sellers`,
    SELLER: userId => `${V1}/admin/odp/sellers/${userId}`,
    PROVISION: userId => `${V1}/admin/odp/sellers/${userId}/provision`,
    REVEAL: userId => `${V1}/admin/odp/sellers/${userId}/intake-key/reveal`,
    SUBMISSIONS: `${V1}/admin/odp/submissions`,
  }),

  // Admin — super-admin "shop reset" tool. DESTRUCTIVE: resets one
  // (user, template, mockup) on a platform — hard-deletes designs, renames
  // NAS files, wipes + regenerates mockups, and replaces/pauses platform
  // listings. Selection endpoints feed the picker; DRY_RUN returns the
  // read-only plan; START kicks off the async job (202 + job_id) which the
  // panel polls via STATUS and unblocks via RESOLVE on a paused job.
  SHOP_RESET: Object.freeze({
    USERS: `${V1}/admin/shop-reset/users`,
    TEMPLATES: `${V1}/admin/shop-reset/templates`,
    MOCKUPS: `${V1}/admin/shop-reset/mockups`,
    DRY_RUN: `${V1}/admin/shop-reset/dry-run`,
    START: `${V1}/admin/shop-reset/start`,
    STATUS: jobId => `${V1}/admin/shop-reset/status/${jobId}`,
    RESOLVE: jobId => `${V1}/admin/shop-reset/resolve/${jobId}`,
    // Escape hatch — flips a non-terminal job (queued/running/paused) to
    // 'cancelled' so the in-flight guard frees up and START can run again.
    // 400 if the job is already terminal; 404 if unknown.
    CANCEL: jobId => `${V1}/admin/shop-reset/cancel/${jobId}`,
    // Pick-able listings for a paused job's resolution dropdown. Returns ALL
    // active/draft/inactive (not deleted) listings for the shop owner, so an
    // admin can bind an INACTIVE listing the auto-match skipped. Optional
    // case-insensitive title `search`.
    LISTINGS: (userId, search) =>
      `${V1}/admin/shop-reset/listings?user_id=${userId}${search ? `&search=${encodeURIComponent(search)}` : ''}&limit=200`,
    // Pull the target shop's listings LIVE from Etsy into the local cache the
    // LISTINGS dropdown reads — for a shop the background sync never populated.
    // 409 if a refresh is already running; 502 on Etsy auth failure.
    REFRESH_LISTINGS: userId => `${V1}/admin/shop-reset/refresh-listings?user_id=${userId}`,
  }),

  // Admin mockup regeneration → NAS + watermark edit. Reuses the SHOP_RESET
  // user/template/mockup pickers for selection.
  MOCKUP_REGEN: Object.freeze({
    PREVIEW: `${V1}/admin/mockup-regen/preview`,
    START: `${V1}/admin/mockup-regen/start`,
    STATUS: jobId => `${V1}/admin/mockup-regen/status/${jobId}`,
    WATERMARK: mockupId => `${V1}/admin/mockup-regen/mockup/${mockupId}/watermark`,
  }),

  // Standalone admin listing-image replace (range-selectable). Reuses the
  // SHOP_RESET user/template/mockup pickers + REFRESH_LISTINGS ("Pull from Etsy").
  LISTING_REPLACE: Object.freeze({
    PREVIEW: `${V1}/admin/listing-replace/preview`,
    START: `${V1}/admin/listing-replace/start`,
    STATUS: jobId => `${V1}/admin/listing-replace/status/${jobId}`,
  }),

  // AX (Agent Experience)
  AX: Object.freeze({
    // Contracts
    CONTRACTS_ENDPOINT: `${V1}/ax/contracts/endpoint`,
    CONTRACTS_SERVICE: `${V1}/ax/contracts/service`,
    CONTRACTS_API: `${V1}/ax/contracts/api`,
    CONTRACTS_DISCOVER: `${V1}/ax/contracts/discover`,
    CONTRACTS_VALIDATE: `${V1}/ax/contracts/validate`,
    // Rules
    RULES_WORKFLOW: `${V1}/ax/rules/workflow`,
    RULES_PERMISSION: `${V1}/ax/rules/permission`,
    RULES_BUSINESS: `${V1}/ax/rules/business`,
    RULES_EVALUATE: `${V1}/ax/rules/evaluate`,
    RULES_FOR_OPERATION: `${V1}/ax/rules/for-operation`,
    // Artifacts
    ARTIFACTS: `${V1}/ax/artifacts`,
    ARTIFACTS_STATS: `${V1}/ax/artifacts/stats`,
    ARTIFACTS_AUDIT_TRAIL: `${V1}/ax/artifacts/audit-trail`,
    ARTIFACTS_BY_SESSION: `${V1}/ax/artifacts/by-session`,
    ARTIFACTS_BY_OPERATION: `${V1}/ax/artifacts/by-operation`,
    // Discovery manifest — scope vocabulary + auth flow (any authed principal).
    // Used to populate the scope picker + risk-tier options dynamically.
    MANIFEST: `${V1}/ax/manifest`,
    // Agent key management (super-admin only). CREATE/ROTATE return the plaintext
    // cfk_ secret exactly once; LIST/GET never return it.
    AGENT_KEYS: `${V1}/ax/agent/keys`,
    AGENT_KEY: keyId => `${V1}/ax/agent/keys/${keyId}`,
    AGENT_KEY_ROTATE: keyId => `${V1}/ax/agent/keys/${keyId}/rotate`,
  }),
});

export default API_ENDPOINTS;
